import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import { colors } from "../utils/datosTheme";
import { formatMoney, formatNumber } from "../utils/datosFormatters";

function percent(value) {
  return `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 2 })}%`;
}

function days(value) {
  return `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })}d`;
}

const tableCellSx = {
  color: colors.ink,
  fontSize: 12,
  borderColor: colors.line,
  whiteSpace: "nowrap",
};

function PortfolioToggle({ value, onChange }) {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_, next) => next && onChange(next)}
      sx={{
        "& .MuiToggleButton-root": {
          minWidth: 172,
          height: 38,
          px: 1.6,
          py: 0,
          color: colors.blue,
          borderColor: colors.line,
          backgroundColor: "rgba(255,255,255,0.88)",
          fontSize: 13,
          fontWeight: 850,
          lineHeight: 1.2,
          textTransform: "none",
          "&.Mui-selected": {
            bgcolor: colors.blue,
            color: "#fff",
            borderColor: colors.blue,
            boxShadow: "none",
            "&:hover": { bgcolor: "#004be0", borderColor: "#004be0" },
          },
        },
      }}
    >
      <ToggleButton value="asignados" aria-label="leads-asignados">Leads asignados</ToggleButton>
      <ToggleButton value="cartera" aria-label="estado-cartera">Estado de cartera</ToggleButton>
    </ToggleButtonGroup>
  );
}

function PortfolioChartTitle({ value, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      <PortfolioToggle value={value} onChange={onChange} />
      <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15 }}>
        por vendedor
      </Typography>
    </Box>
  );
}

export default function VendedoresDashboard({ data }) {
  const [portfolioMetric, setPortfolioMetric] = useState("asignados");
  const kpis = data?.kpis || {};
  const currency = kpis.moneda_principal;
  const vendedores = data?.vendedores || [];

  const hasAssigned = vendedores.some((item) => Number(item.leads_asignados || 0) > 0);
  const hasPortfolio = vendedores.some(
    (item) =>
      Number(item.activos || 0) > 0 ||
      Number(item.ventas || 0) > 0 ||
      Number(item.bajas || 0) > 0 ||
      Number(item.futuros || 0) > 0
  );
  const hasConversion = vendedores.some((item) => Number(item.conversion_pct || 0) > 0);
  const hasCloseTime = vendedores.some((item) => Number(item.tiempo_promedio_cierre_dias || 0) > 0);

  const kpiDescriptions = {
    vendedores_activos: "Vendedores activos de la empresa",
    leads_asignados: "Consultas asignadas en el período",
    leads_activos: "Consultas activas actualmente",
    ventas_cerradas: "Ventas registradas en el período",
    bajas: "Consultas cerradas como baja en el período",
    futuros: "Consultas programadas a futuro en el período",
    conversion_pct: "Ventas / leads asignados",
    mejor_vendedor: "Score: 35% ventas + 35% monto + 30% conversión",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "vendedores_activos",
          "leads_asignados",
          "leads_activos",
          "ventas_cerradas",
          "bajas",
          "futuros",
          "conversion_pct",
          "mejor_vendedor",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={id === "mejor_vendedor" ? (kpis[id] || "Sin datos") : kpis[id]}
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel
          title={<PortfolioChartTitle value={portfolioMetric} onChange={setPortfolioMetric} />}
          empty={portfolioMetric === "asignados" ? !hasAssigned : !hasPortfolio}
        >
          {portfolioMetric === "asignados" ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={vendedores} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
                <CartesianGrid stroke={colors.softLine} horizontal={false} />
                <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="vendedor_nombre" width={108} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
                <RechartsTooltip formatter={(value) => formatNumber(value)} />
                <Bar dataKey="leads_asignados" name="Leads asignados" fill={colors.blue} radius={[0, 5, 5, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={vendedores} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
                <CartesianGrid stroke={colors.softLine} horizontal={false} />
                <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
                <YAxis type="category" dataKey="vendedor_nombre" width={108} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
                <RechartsTooltip formatter={(value) => formatNumber(value)} />
                <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                <Bar dataKey="activos" stackId="cartera" name="Activos" fill={colors.blue} radius={[0, 0, 0, 0]} />
                <Bar dataKey="ventas" stackId="cartera" name="Ventas" fill={colors.green} radius={[0, 0, 0, 0]} />
                <Bar dataKey="bajas" stackId="cartera" name="Bajas" fill={colors.coral} radius={[0, 0, 0, 0]} />
                <Bar dataKey="futuros" stackId="cartera" name="Futuros" fill={colors.amber} radius={[0, 5, 5, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel title="Conversión por vendedor" empty={!hasConversion}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={vendedores} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} tickFormatter={(value) => `${value}%`} />
              <YAxis type="category" dataKey="vendedor_nombre" width={108} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => percent(value)} />
              <Bar dataKey="conversion_pct" name="Conversión" fill={colors.green} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Tiempo promedio de cierre por vendedor" empty={!hasCloseTime}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={vendedores} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} tickFormatter={(value) => `${value}d`} />
              <YAxis type="category" dataKey="vendedor_nombre" width={108} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => `${Number(value || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })} días`} />
              <Bar dataKey="tiempo_promedio_cierre_dias" name="Tiempo cierre" fill={colors.amber} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{
            border: `1px solid ${colors.line}`,
            borderRadius: "8px",
            backgroundColor: colors.panel,
            boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
            p: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15, mb: 1.5 }}>
            Ranking y rendimiento por vendedor
          </Typography>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Vendedor", "Score", "Leads", "Activos", "Ventas", "Bajas", "Futuros", "Conversión", "Monto", "Ticket", "Cierre"].map((head) => (
                    <TableCell
                      key={head}
                      align={head === "Vendedor" ? "left" : "right"}
                      sx={{
                        color: colors.ink,
                        fontWeight: 950,
                        fontSize: 12,
                        bgcolor: "#f7f7f9",
                        borderColor: colors.line,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vendedores.map((row) => (
                  <TableRow key={row.vendedor_id}>
                    <TableCell sx={{ ...tableCellSx, fontWeight: 850 }}>{row.vendedor_nombre}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.score)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.leads_asignados)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.activos)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.ventas)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.bajas)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatNumber(row.futuros)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{percent(row.conversion_pct)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatMoney(row.monto_vendido, currency)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{formatMoney(row.ticket_promedio, currency)}</TableCell>
                    <TableCell align="right" sx={tableCellSx}>{days(row.tiempo_promedio_cierre_dias)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
